/**
 * AutomataCoordinator - Manages the cellular automata system
 *
 * Responsible for:
 * - Double-buffering two 2D arrays (tileMap, entityMap) storing BaseTile objects
 * - Processing entity ticks with move() calls
 * - Applying MoveResults atomically
 * - Synchronizing Phaser sprites with entity positions
 */

import Phaser from "phaser";
import { GAME_CONFIG } from "../config";
import { TILE, getExplosionColors, isArrowTile } from "../constants/gameConstants";
import { ArrowTile } from "../tiles/ArrowTile";
import { BallEntity } from "../tiles/BallEntity";
import {
  BaseTile,
  MoveContext,
  MoveResult,
  MoveResult_Burn,
  MoveResult_CollectFlippers,
  MoveResult_CollectKey,
  MoveResult_CollectStar,
  MoveResult_CollectSuctionBoots,
  MoveResult_Die,
  MoveResult_Drown,
  MoveResult_HideHint,
  MoveResult_Move,
  MoveResult_OpenDoor,
  MoveResult_PressButton,
  MoveResult_Push,
  MoveResult_ReleaseButton,
  MoveResult_ShowHint,
  MoveResult_StepOnSand,
  MoveResult_TriggerRotation,
  MoveResult_Win
} from "../tiles/BaseTile";
import { ButtonDoorTile } from "../tiles/ButtonDoorTile";
import { ButtonTile } from "../tiles/ButtonTile";
import { PlayerEntity } from "../tiles/PlayerEntity";
import { createTileInstance } from "../tiles/registry";
import { WaterBlockEntity } from "../tiles/WaterBlockEntity";
import { WaterTile } from "../tiles/WaterTile";
import { TileIndex } from "../types";
import { Direction } from "../utils/Direction";
import { logger } from "../utils/logger";
import { GlobalState } from "./GlobalState";

export interface AutomataCoordinatorCallbacks {
  onPlayerDeath: () => void;
  onLevelWin: () => void;
  onShowHint: (hintText: string) => void;
  onHideHint: () => void;
}

export class AutomataCoordinator {
  // Current state (read-only during tick processing)
  private currentTileMap: (BaseTile | null)[][];
  private currentEntityMap: (BaseTile | null)[][];

  // Next state (buffer being written to during tick)
  private nextTileMap: (BaseTile | null)[][];
  private nextEntityMap: (BaseTile | null)[][];

  // Phaser scene reference
  private scene: Phaser.Scene;

  // Global game state
  private globalState: GlobalState;

  // Callbacks for game events
  private callbacks: AutomataCoordinatorCallbacks;

  // All sprites (both static tiles and entities) - for visual rendering only
  private allSprites: Phaser.GameObjects.Sprite[] = [];

  // Tick state
  private tickNumber: number = 0;
  private lastTickTime: number = 0;

  // Game state flags
  private hasWon: boolean = false;

  // Level hint text
  private levelHint: string | undefined;

  constructor(
    scene: Phaser.Scene,
    levelData: TileIndex[][],
    globalState: GlobalState,
    callbacks: AutomataCoordinatorCallbacks,
    levelHint?: string
  ) {
    this.scene = scene;
    this.globalState = globalState;
    this.callbacks = callbacks;
    this.levelHint = levelHint;

    // Initialize current state - convert TileIndex[][] to BaseTile[][]
    this.currentTileMap = this.createTileMapFromLevelData(levelData);
    this.currentEntityMap = this.createEmptyMap<BaseTile | null>(
      levelData.length,
      levelData[0]?.length || 0,
      null
    );

    // Initialize buffers
    this.nextTileMap = this.createEmptyMap<BaseTile | null>(
      levelData.length,
      levelData[0]?.length || 0,
      null
    );
    this.nextEntityMap = this.createEmptyMap<BaseTile | null>(
      levelData.length,
      levelData[0]?.length || 0,
      null
    );

    // Preprocess: Replace entity tiles with empty tiles so they render underneath
    this.preprocessEntityTiles(levelData);

    // Scan levelData and create initial entities (player, bees, bugs, etc.)
    this.initializeEntitiesFromLevel(levelData);

    // Initial render of all tiles and entities
    this.render();
  }

  /**
   * Main tick function - called by InputSystem
   * @param currentTime - Current timestamp in milliseconds
   * @param playerDirection - Optional direction from keyboard input to queue on player
   */
  processTick(currentTime: number, playerDirection?: Direction): void {
    const deltaMs = currentTime - this.lastTickTime;
    this.lastTickTime = currentTime;
    this.tickNumber++;

    // Step 1: Deep clone current state to next state
    // Clone tiles (static tiles that don't move)
    this.nextTileMap = this.deepClone2DArray(this.currentTileMap);

    // Clone entities (pass-by-value semantics - each entity gets cloned)
    this.nextEntityMap = this.deepClone2DArray(this.currentEntityMap);


    // If player direction provided, queue it on the player
    if (playerDirection) {
      const player = this.getNextPlayer();
      if (player) {
        player.queueMove(playerDirection);
      }
    }

    // Step 2: Build read-only context
    const context = new MoveContext(
      (col, row) => this.getTileAt(col, row),
      (col, row) => this.getEntityAt(col, row),
      deltaMs,
      this.globalState,
      () => {
        const player = this.getNextPlayer();
        return player ? player.coordinates() : null;
      }
    );

    // Step 3: Process each entity
    const moveResults: Array<{ entity: BaseTile; result: MoveResult }> = [];

    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const entity = this.nextEntityMap[row][col];
        if (entity) {
          const result = entity.move(context);
          moveResults.push({ entity, result });
        }
      }
    }

    // Step 4: Apply move results to next state buffers
    for (const { entity, result } of moveResults) {
      this.applyMoveResult(entity, result);
    }

    // Step 4.5: Update button door states
    this.updateButtonDoorStates();

    // Step 5: Handle collisions (entities at same position)
    // TODO: Implement collision resolution

    // Step 6: Atomic swap - current becomes next
    this.currentTileMap = this.nextTileMap;
    this.currentEntityMap = this.nextEntityMap;

    // Step 7: Render all tiles and entities from current state
    this.render();
  }

  private isOOB(col: number, row: number): boolean {
    return (
      row < 0 ||
      row >= this.levelHeight() ||
      col < 0 ||
      col >= this.levelWidth()
    );
  }

  /**
   * Apply a MoveResult to the next state buffers
   */
  private applyMoveResult(entity: BaseTile, result: MoveResult): void {
    //logger.info(`[Tick ${this.tickNumber}] Applying MoveResult for entity at (${entity.col}, ${entity.row}):`, JSON.stringify(result));
    if (entity instanceof BallEntity && result.type !== "wait" && result.type !== "wall") {
      logger.debug("applyMoveResult", { entity: entity.tileType(), result: result.type });
    }
    switch (result.type) {
      case "move":
        this.applyMoveResult_move(entity, result);
        break;
      case "die":
        this.applyMoveResult_die(entity, result);
        break;
      case "push":
        this.applyMoveResult_push(entity, result);
        break;
      case "collectStar":
        this.applyMoveResult_collectStar(entity, result);
        break;
      case "collectKey":
        this.applyMoveResult_collectKey(entity, result);
        break;
      case "collectFlippers":
        this.applyMoveResult_collectFlippers(entity, result);
        break;
      case "collectSuctionBoots":
        this.applyMoveResult_collectSuctionBoots(entity, result);
        break;
      case "openDoor":
        this.applyMoveResult_openDoor(entity, result);
        break;
      case "stepOnSand":
        this.applyMoveResult_stepOnSand(entity, result);
        break;
      case "pressButton":
        this.applyMoveResult_pressButton(entity, result);
        break;
      case "releaseButton":
        this.applyMoveResult_releaseButton(entity, result);
        break;
      case "triggerRotation":
        this.applyMoveResult_triggerRotation(entity, result);
        break;
      case "win":
        this.applyMoveResult_win(entity, result);
        break;
      case "showHint":
        this.applyMoveResult_showHint(entity, result);
        break;
      case "hideHint":
        this.applyMoveResult_hideHint(entity, result);
        break;
      case "burn":
      case "drown":
        this.applyMoveResult_burn_or_drown(entity, result);
        break;
      case "wait":
      case "wall":
        // Entity stays in place, no changes needed
        // (clone already exists in nextEntityMap with updated internal state)
        break;
    }
  }

  private applyMoveResult_move(entity: BaseTile, result: MoveResult_Move): void {
    // Get cloned entity from nextEntityMap
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    if (entity instanceof WaterBlockEntity) {
      const waterBlock = entity as WaterBlockEntity;
      // Calculate target position
      const targetCol = entity.col + result.direction.toVector().dx;
      const targetRow = entity.row + result.direction.toVector().dy;

      // Check if landing on water tile
      const targetTile = this.currentTileMap[targetRow]?.[targetCol];
      logger.debug("Water block moving:", {
        from: { row: entity.row, col: entity.col },
        to: { row: targetRow, col: targetCol },
        targetTile: targetTile?.tileType(),
      });

      if (targetTile instanceof WaterTile) {
        // Water block fills water and disappears
        this.nextTileMap[targetRow][targetCol] = createTileInstance(TILE.EMPTY, targetCol, targetRow);
        waterBlock.destroySprite();

        // Clear water block from its current position (not target)
        this.nextEntityMap[entity.row][entity.col] = null;
        return;
      }
    }
    // Move entity to new position
    this.moveTile(this.nextEntityMap, nextEntity, result.direction);
  }

  private applyMoveResult_die(entity: BaseTile, result: MoveResult_Die): void {
    // Calculate target position
    const offset = result.direction.toVector();
    const targetCol = entity.col + offset.dx;
    const targetRow = entity.row + offset.dy;

    if (result.who === "self") {
      // Only this entity dies
      const playerX = entity.x();
      const playerY = entity.y();

      this.nextEntityMap[entity.row][entity.col] = null;
      entity.destroySprite();

      // If it's the player, create explosion, set death flag, and trigger callback
      if (entity instanceof PlayerEntity) {
        this.createExplosion(playerX, playerY);
        this.callbacks.onPlayerDeath();
      }
    } else if (result.who === "other") {
      // This entity kills target and moves there
      const targetEntity = result.collisionWith === "tile" ? this.nextTileMap[targetRow]?.[targetCol] : this.nextEntityMap[targetRow]?.[targetCol];
      if (targetEntity) {
        this.createExplosion(targetEntity.x(), targetEntity.y());
        targetEntity.destroySprite();
      }

      if (result.collisionWith === "entity") {
        this.nextEntityMap[targetRow][targetCol] = null;
      } else {
        this.nextTileMap[targetRow][targetCol] = createTileInstance(TILE.EMPTY, targetCol, targetRow);
      }

      // Move to target position
      this.moveTile(this.nextEntityMap, entity, result.direction);

    } else if (result.who === "both") {
      // Both entities die
      const playerX = entity.x();
      const playerY = entity.y();

      this.nextEntityMap[entity.row][entity.col] = null;
      entity.destroySprite();
      this.createExplosion(playerX, playerY);
      // If it's the player, create explosion, set death flag, and trigger callback
      if (entity instanceof PlayerEntity) {
        this.callbacks.onPlayerDeath();
      }

      const targetEntity = result.collisionWith === "tile" ? this.nextTileMap[targetRow]?.[targetCol] : this.nextEntityMap[targetRow]?.[targetCol];
      if (targetEntity) {
        this.createExplosion(targetEntity.x(), targetEntity.y());
        if (result.collisionWith === "entity") {
          this.nextEntityMap[targetRow][targetCol] = null;
        } else {
          this.nextTileMap[targetRow][targetCol] = createTileInstance(TILE.EMPTY, targetCol, targetRow);
        }
        targetEntity.destroySprite();
      }
      if (entity instanceof PlayerEntity || targetEntity instanceof PlayerEntity) {
        this.callbacks.onPlayerDeath();
      }

    }
  }

  private applyMoveResult_push_waterblock({ entity, result, thingWerePushing }: { entity: BaseTile, result: MoveResult_Push, thingWerePushing: WaterBlockEntity }): void {
    const offset = result.direction.toVector();
    const { col: targetCol, row: targetRow } = entity.coordsAtDirection(result.direction);
    const tileUnderThing = this.nextTileMap[targetRow][targetCol];

    if (!tileUnderThing) {
      logger.debug("[DEBUG] Push failed: No tile under water block.", { entity, result, targetCol, targetRow, tileUnderThing });
      return;
    }

    // Check where the water block would move to
    const waterBlockNewCol = targetCol + offset.dx;
    const waterBlockNewRow = targetRow + offset.dy;

    // Check bounds
    if (this.isOOB(waterBlockNewCol, waterBlockNewRow)) {
      logger.debug("[DEBUG] Push failed: Water block push out of bounds.", { waterBlockNewCol, waterBlockNewRow });
      // Can't push out of bounds - treat as wall
      return;
    }

    const waterBlockTargetTile =
      this.nextTileMap[waterBlockNewRow][waterBlockNewCol];
    const waterBlockTargetEntity =
      this.nextEntityMap[waterBlockNewRow][waterBlockNewCol];

    if (!waterBlockTargetTile) {
      logger.debug("[DEBUG] Push failed: Water block target tile is null.", { waterBlockTargetTile, waterBlockTargetEntity });
      return;
    }

    if (
      !thingWerePushing.canBePushed(waterBlockTargetTile.tileType(), waterBlockTargetEntity?.tileType() ?? null)
    ) {
      logger.debug(`[DEBUG] Push failed: water block cannot be pushed to target.`, { waterBlockTargetTile, waterBlockTargetEntity });
      // Can't push - treat as wall
      return;
    }

    if (tileUnderThing instanceof ButtonTile) {
      // If pushing off a button, release it
      this.globalState.releaseButton();
    }

    // Check if water block is being pushed into water
    if (waterBlockTargetTile instanceof WaterTile) {
      // Water block fills water and disappears
      this.nextTileMap[waterBlockNewRow][waterBlockNewCol] = createTileInstance(TILE.EMPTY, waterBlockNewCol, waterBlockNewRow);
      thingWerePushing.destroySprite();

      // Clear water block from its position
      this.nextEntityMap[targetRow][targetCol] = null;

    } else {
      // Normal push - move both entities
      // Move water block to new position
      this.moveTile(this.nextEntityMap, thingWerePushing, result.direction);
    }
    if (waterBlockTargetTile instanceof ButtonTile) {
      this.globalState.pressButton();
    }
    if ((entity instanceof WaterBlockEntity || entity instanceof BallEntity) && !isArrowTile(tileUnderThing.tileType())) {
      entity.clearForcedDirection();
      if (entity instanceof BallEntity && result.withMomentum) {
        entity.stopMoving();
      }

    }

    if (entity instanceof PlayerEntity || entity instanceof WaterBlockEntity || entity instanceof BallEntity) {
      // todo add ability to queue move for other stuff that can push water blocks or balls
      // this code moves the player, checks what tile was under the block/ball, and reacts to it
      entity.queueMove(result.direction);
      const newResult = entity.move(new MoveContext(
        (col, row) => this.getTileAt(col, row, this.nextTileMap),
        (col, row) => this.getEntityAt(col, row, this.nextEntityMap),
        0,
        this.globalState,
        () => {
          const player = this.getNextPlayer();
          return player ? player.coordinates() : null;
        }));
      this.applyMoveResult(entity, newResult);
    } else {
      this.moveTile(this.nextEntityMap, entity, result.direction);
    }
  }

  private applyMoveResult_push_ball({ entity, result, thingWerePushing }: { entity: BaseTile, result: MoveResult_Push, thingWerePushing: BallEntity }): void {
    const offset = result.direction.toVector();
    const { col: targetCol, row: targetRow } = entity.coordsAtDirection(result.direction);
    const tileUnderThing = this.nextTileMap[targetRow][targetCol];

    if (!tileUnderThing) {
      logger.debug("[DEBUG] Push failed: No tile under ball.", { entity, result, targetCol, targetRow, tileUnderThing });
      return;
    }

    logger.debug(`${entity.tileType()} pushing ball id ${thingWerePushing.id} from (${targetCol}, ${targetRow}) to direction ${result.direction.value}`);

    // Check where the ball would move to
    const ballNewCol = targetCol + offset.dx;
    const ballNewRow = targetRow + offset.dy;

    // Check bounds
    if (this.isOOB(ballNewCol, ballNewRow)) {
      logger.debug("[DEBUG] Push failed: ball push out of bounds.", { ballNewCol, ballNewRow });
      // Can't push out of bounds - treat as wall
      return;
    }

    const ballTargetTile =
      this.nextTileMap[ballNewRow][ballNewCol];
    const ballTargetEntity =
      this.nextEntityMap[ballNewRow][ballNewCol];

    if (!ballTargetTile) {
      logger.debug("[DEBUG] Push failed: ball target tile is null.", { ballTargetTile, ballTargetEntity });
      return;
    }

    if (
      !thingWerePushing.canBePushed(ballTargetTile.tileType(), ballTargetEntity?.tileType() ?? null)
    ) {
      logger.debug(`[DEBUG] Push failed: ball cannot be pushed to target.`, { ballTargetTile, ballTargetEntity });
      // Can't push - treat as wall
      return;
    }

    if (tileUnderThing instanceof ButtonTile) {
      // If pushing off a button, release it
      this.globalState.releaseButton();
    }
    if (ballTargetTile instanceof ButtonTile) {
      this.globalState.pressButton();
    }
    if ((entity instanceof WaterBlockEntity || entity instanceof BallEntity) && !isArrowTile(tileUnderThing.tileType())) {
      entity.clearForcedDirection();
    }

    thingWerePushing.startMoving(result.direction);
    this.moveTile(this.nextEntityMap, thingWerePushing, result.direction);

    if (entity instanceof PlayerEntity) {
      // todo add ability to queue move for other stuff that can push balls or balls
      // this code moves the player, checks what tile was under the block/ball, and reacts to it
      entity.queueMove(result.direction);
      const newResult = entity.move(new MoveContext(
        (col, row) => this.getTileAt(col, row, this.nextTileMap),
        (col, row) => this.getEntityAt(col, row, this.nextEntityMap),
        0,
        this.globalState,
        () => {
          const player = this.getNextPlayer();
          return player ? player.coordinates() : null;
        }));
      this.applyMoveResult(entity, newResult);
    } else if (entity instanceof BallEntity) {
      // Ball pushed another ball - move the pushing ball forward and stop it
      this.moveTile(this.nextEntityMap, entity, result.direction);
      entity.stopMoving();
      entity.clearForcedDirection();
    } else {
      this.moveTile(this.nextEntityMap, entity, result.direction);
    }
  }

  private applyMoveResult_push(entity: BaseTile, result: MoveResult_Push): void {
    // Entity is trying to push something (e.g., water block)
    const offset = result.direction.toVector();
    const { col: targetCol, row: targetRow } = entity.coordsAtDirection(result.direction);
    const thingWerePushing = this.nextEntityMap[targetRow][targetCol];
    if (thingWerePushing instanceof WaterBlockEntity) {
      this.applyMoveResult_push_waterblock({ entity, result, thingWerePushing });
    } else if (thingWerePushing instanceof BallEntity) {
      this.applyMoveResult_push_ball({ entity, result, thingWerePushing });
    } else {
      // Can't push - treat as wall
      logger.debug("[DEBUG] Push failed: No pushable entity found.", { entity, result, targetCol, targetRow, thingWerePushing });
      return;
    }
  }

  private applyMoveResult_collectStar(entity: BaseTile, result: MoveResult_CollectStar): void {
    // Move entity and collect star
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Update global state
    this.globalState.collectStar();

    // Replace star tile with empty
    this.nextTileMap[nextEntity.row][nextEntity.col] = createTileInstance(
      TILE.EMPTY,
      nextEntity.col,
      nextEntity.row
    );
  }

  private applyMoveResult_collectKey(entity: BaseTile, result: MoveResult_CollectKey): void {
    // Move entity and collect key
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Update global state
    this.globalState.addKey(result.keyColor);

    // Replace key tile with empty
    this.nextTileMap[nextEntity.row][nextEntity.col] = createTileInstance(
      TILE.EMPTY,
      nextEntity.col,
      nextEntity.row
    );
  }

  private applyMoveResult_collectFlippers(entity: BaseTile, result: MoveResult_CollectFlippers): void {
    // Move entity and collect flippers
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Update global state
    this.globalState.hasFlippers = true;

    // Replace flippers tile with empty
    this.nextTileMap[nextEntity.row][nextEntity.col] = createTileInstance(
      TILE.EMPTY,
      nextEntity.col,
      nextEntity.row
    );
  }

  private applyMoveResult_collectSuctionBoots(entity: BaseTile, result: MoveResult_CollectSuctionBoots): void {
    // Move entity and collect suction boots
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Update global state
    this.globalState.hasSuctionBoots = true;

    // Replace suction boots tile with empty
    this.nextTileMap[nextEntity.row][nextEntity.col] = createTileInstance(
      TILE.EMPTY,
      nextEntity.col,
      nextEntity.row
    );
  }

  private applyMoveResult_openDoor(entity: BaseTile, result: MoveResult_OpenDoor): void {
    // Move entity and open door
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Remove key from inventory (key is consumed when door is opened)
    this.globalState.removeKey(result.doorColor);

    // Replace door with empty
    this.nextTileMap[nextEntity.row][nextEntity.col] = createTileInstance(
      TILE.EMPTY,
      nextEntity.col,
      nextEntity.row
    );
  }

  private applyMoveResult_stepOnSand(entity: BaseTile, result: MoveResult_StepOnSand): void {
    // Move entity onto sand - sand disappears
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Replace sand tile with empty
    this.nextTileMap[nextEntity.row][nextEntity.col] = createTileInstance(
      TILE.EMPTY,
      nextEntity.col,
      nextEntity.row
    );
  }

  private applyMoveResult_pressButton(entity: BaseTile, result: MoveResult_PressButton): void {
    // Move entity onto button
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Press button
    this.globalState.pressButton();
  }

  private applyMoveResult_releaseButton(entity: BaseTile, result: MoveResult_ReleaseButton): void {
    // Entity left button (this is called when entity moves OFF a button)
    // The entity has already moved, just release the button
    this.globalState.releaseButton();
    this.moveTile(this.nextEntityMap, entity, result.direction);
  }

  private applyMoveResult_triggerRotation(entity: BaseTile, result: MoveResult_TriggerRotation): void {
    // Move entity onto rotation button and rotate all arrows
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Rotate all arrows in the tile map
    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const tile = this.nextTileMap[row][col];
        if (tile instanceof ArrowTile) {
          tile.rotate();
        }
      }
    }
  }

  private applyMoveResult_win(entity: BaseTile, result: MoveResult_Win): void {
    // Move entity to exit and trigger win
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Set win flag and trigger callback
    logger.debug("[WIN] Level complete!");
    this.hasWon = true;
    this.callbacks.onLevelWin();
  }

  private applyMoveResult_showHint(entity: BaseTile, result: MoveResult_ShowHint): void {
    // Move entity onto hint tile and show hint
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) return;

    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Trigger hint display callback with hint text
    if (this.levelHint) {
      this.callbacks.onShowHint(this.levelHint);
    }
  }

  private applyMoveResult_hideHint(entity: BaseTile, result: MoveResult_HideHint): void {
    this.moveTile(this.nextEntityMap, entity, result.direction);

    // Trigger hint hide callback
    this.callbacks.onHideHint();
  }

  private applyMoveResult_burn_or_drown(entity: BaseTile, result: MoveResult_Burn | MoveResult_Drown): void {
    logger.debug(`[DEATH] Processing ${result.type} for entity at (${entity.col}, ${entity.row})`);

    // Entity dies from hazard
    const nextEntity = this.nextEntityMap[entity.row][entity.col];
    if (!nextEntity) {
      logger.debug(`[DEATH] No nextEntity found, breaking`);
      return;
    }

    // Move entity to hazard position
    this.moveTile(this.nextEntityMap, nextEntity, result.direction);

    // Calculate explosion position at hazard tile (where entity moved to)
    const playerX = nextEntity.x();
    const playerY = nextEntity.y();
    logger.debug(`[DEATH] Entity is now at (${nextEntity.col}, ${nextEntity.row}), explosion position: (${playerX}, ${playerY})`);

    // Destroy entity at hazard position
    this.nextEntityMap[nextEntity.row][nextEntity.col] = null;
    entity.destroySprite();

    // If it's the player, create explosion, set death flag, and trigger callback
    if (entity instanceof PlayerEntity) {
      logger.debug(`[DEATH] Entity is PlayerEntity, creating explosion`);
      this.createExplosion(playerX, playerY);
      this.callbacks.onPlayerDeath();
    } else {
      logger.debug(`[DEATH] Entity is NOT PlayerEntity, type:`, entity.constructor.name);
    }

    logger.debug(`[DEATH] Entity died from ${result.type}`);
  }

  /**
   * Update button door states based on global button state
   * Called after applying MoveResults to sync door open/closed states
   */
  private updateButtonDoorStates(): void {
    const buttonsPressed = this.globalState.numButtonsPressed();

    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const tile = this.nextTileMap[row][col];
        if (tile instanceof ButtonDoorTile) {
          tile.setOpenBasedOnButtonsPressed(buttonsPressed);
        }
      }
    }
  }

  /**
   * Render all tiles and entities from current tilemaps
   * Destroys existing sprites and recreates them from scratch
   */
  private render(): void {
    // Step 1: Destroy all existing sprites
    for (const sprite of this.allSprites) {
      sprite.destroy();
    }
    this.allSprites = [];

    // Step 2: Render static tiles (walls, arrows, doors, collectibles, etc.)
    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const tile = this.currentTileMap[row][col];
        if (!tile || tile.isEntity()) continue;

        const x = col * GAME_CONFIG.tileSize + GAME_CONFIG.tileSize / 2;
        const y = row * GAME_CONFIG.tileSize + GAME_CONFIG.tileSize / 2;

        const textureKey = this.getTextureKeyForTile(tile);
        const sprite = this.scene.add.sprite(x, y, textureKey);
        sprite.setDepth(0); // Static tiles render below entities

        this.allSprites.push(sprite);
      }
    }

    // Step 3: Render entities (player, bees, blocks, etc.)
    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const entity = this.currentEntityMap[row][col];
        if (!entity) continue;

        const x = entity.col * GAME_CONFIG.tileSize + GAME_CONFIG.tileSize / 2;
        const y = entity.row * GAME_CONFIG.tileSize + GAME_CONFIG.tileSize / 2;

        const textureKey = this.getTextureKeyForTile(entity);
        const sprite = this.scene.add.sprite(x, y, textureKey);
        this.scene.physics.add.existing(sprite);
        sprite.setDepth(10); // Entities render above static tiles

        // Store sprite reference on entity for camera following
        entity.sprite = sprite;

        this.allSprites.push(sprite);
      }
    }

    // Step 4: Update camera to follow the new player sprite
    this.updateCameraFollow();
  }

  /**
   * Create explosion particles at given position
   */
  private createExplosion(x: number, y: number): void {
    logger.debug(`[EXPLOSION] Creating explosion at (${x}, ${y})`);
    const particleCount = 12;
    const colors = getExplosionColors();

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const speed = 80 + Math.random() * 60;
      const color = colors[Math.floor(Math.random() * colors.length)];

      const particle = this.scene.add.circle(x, y, 4 + Math.random() * 4, color);
      particle.setDepth(50); // Render above tiles (0-10) but below UI (100+)
      this.scene.physics.add.existing(particle);

      const particleBody = particle.body as Phaser.Physics.Arcade.Body;
      particleBody.setVelocity(
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      );

      this.scene.tweens.add({
        targets: particle,
        alpha: 0,
        scale: 0.2,
        duration: 600 + Math.random() * 200,
        onComplete: () => particle.destroy(),
      });
    }
  }

  /**
   * Tiles that should be converted to entities and cleared from base tile map
   */
  private static readonly ENTITY_TILES: TileIndex[] = [
    TILE.PLAYER_START,
    TILE.BEE_HORIZONTAL,
    TILE.BEE_VERTICAL,
    TILE.BUG,
    TILE.WATER_BLOCK,
    TILE.CAT,
    TILE.BALL,
  ];

  /**
   * Preprocess tile map: replace entity tiles with empty tiles
   * This ensures empty tiles render underneath entities
   */
  private preprocessEntityTiles(levelData: TileIndex[][]): void {
    for (let row = 0; row < levelData.length; row++) {
      for (let col = 0; col < levelData[row].length; col++) {
        const tile = levelData[row][col];
        if (AutomataCoordinator.ENTITY_TILES.includes(tile)) {
          // Replace entity tile with empty tile
          this.currentTileMap[row][col] = createTileInstance(TILE.EMPTY, col, row);
        }
      }
    }
  }

  /**
   * Scan level data and create entities (player, bees, etc.)
   * Clears entity tiles from base tile map during preprocessing
   * Now uses the tile registry for entity creation
   */
  private initializeEntitiesFromLevel(levelData: TileIndex[][]): void {
    // First pass: find player for CatEntity references
    let playerEntity: PlayerEntity | null = null;
    for (let row = 0; row < levelData.length; row++) {
      for (let col = 0; col < levelData[row].length; col++) {
        if (levelData[row][col] === TILE.PLAYER_START) {
          playerEntity = new PlayerEntity({ col, row });
          this.addEntity(row, col, playerEntity);
          break;
        }
      }
      if (playerEntity) break;
    }

    // Second pass: create all other entities
    for (let row = 0; row < levelData.length; row++) {
      for (let col = 0; col < levelData[row].length; col++) {
        const tile = levelData[row][col];

        // Skip player (already created) and non-entity tiles
        if (tile === TILE.PLAYER_START || !AutomataCoordinator.ENTITY_TILES.includes(tile)) {
          continue;
        }

        // Use registry to create entity
        const entity = createTileInstance(tile, col, row);
        if (!entity || !entity.isEntity()) {
          continue;
        }

        this.addEntity(row, col, entity);
      }
    }
  }

  /**
   * Add an entity to the coordinator
   */
  private addEntity(row: number, col: number, entity: BaseTile): void {
    // Add to entityMap (stores BaseTile object)
    // Sprites will be created by render()
    this.currentEntityMap[row][col] = entity;
  }

  /**
   * Get texture key for a tile and ensure the texture exists
   */
  private getTextureKeyForTile(tile: BaseTile): string {
    const textureKey = tile.textureKey();
    const descriptor = tile.textureDescriptor();

    // Ensure texture exists
    if (!this.scene.textures.exists(textureKey)) {
      if (descriptor.type === "png") {
        this.scene.load.image(textureKey, descriptor.path);
        this.scene.load.start();
      } else if (descriptor.type === "procedural") {
        descriptor.generator(this.scene, textureKey);
      }
    }

    return textureKey;
  }

  /**
   * Get tile at position (read-only) - returns TileIndex derived from BaseTile
   */
  private getTileAt(col: number, row: number, tilemap = this.currentTileMap): TileIndex | null {
    if (
      row < 0 ||
      row >= this.levelHeight() ||
      col < 0 ||
      col >= this.levelWidth()
    ) {
      return null;
    }
    const tile = tilemap[row]?.[col];
    return tile ? tile.tileType() : null;
  }

  /**
   * Get entity at position (read-only) - returns TileIndex derived from BaseTile
   */
  private getEntityAt(col: number, row: number, entityMap = this.currentEntityMap): TileIndex | null {
    if (
      row < 0 ||
      row >= this.levelHeight() ||
      col < 0 ||
      col >= this.levelWidth()
    ) {
      return null;
    }
    const entity = entityMap[row]?.[col];
    return entity ? entity.tileType() : null;
  }

  /**
   * Get level height (derived from currentTileMap)
   */
  private levelHeight(): number {
    return this.currentTileMap.length;
  }

  /**
   * Get level width (derived from currentTileMap)
   */
  private levelWidth(): number {
    return this.currentTileMap[0]?.length || 0;
  }

  /**
   * Move a tile in the specified direction within a tilemap
   * Updates the tile's position and the tilemap
   */
  private moveTile(
    tilemap: (BaseTile | null)[][],
    tile: BaseTile,
    direction: Direction
  ): void {
    tilemap[tile.row][tile.col] = null;
    tile.moveTo(direction);
    tilemap[tile.row][tile.col] = tile;
  }

  /**
   * Deep clone a 2D array (for primitives)
   */
  private deepClone2DArray(arr: (BaseTile | null)[][]): (BaseTile | null)[][] {
    return arr.map((row) => row.map((item) => item?.clone() || null));
  }

  /**
   * Create an empty 2D array filled with a value
   */
  private createEmptyMap<T>(height: number, width: number, value: T): T[][] {
    return Array.from({ length: height }, () => Array(width).fill(value));
  }

  /**
   * Convert TileIndex[][] level data to BaseTile[][] by creating tile instances
   */
  private createTileMapFromLevelData(levelData: TileIndex[][]): (BaseTile | null)[][] {
    const tileMap: (BaseTile | null)[][] = [];
    for (let row = 0; row < levelData.length; row++) {
      tileMap[row] = [];
      for (let col = 0; col < levelData[row].length; col++) {
        const tileIndex = levelData[row][col];
        // Create tile instance using registry, or null for empty
        const tile = createTileInstance(tileIndex, col, row);
        tileMap[row][col] = tile;
      }
    }
    return tileMap;
  }

  /**
   * Set up camera to follow player
   * Calculates all dimensions from level data and game config
   * @param camera - The Phaser camera to configure
   */
  setupCamera(camera: Phaser.Cameras.Scene2D.Camera): void {
    // Calculate world size from level dimensions
    const worldWidth = GAME_CONFIG.tileSize * this.levelWidth();
    const worldHeight = GAME_CONFIG.tileSize * this.levelHeight();

    // Calculate viewport size (max 10x10 tiles + stats panel)
    const maxViewTiles = GAME_CONFIG.viewportSize;
    const viewportWidth = GAME_CONFIG.tileSize * Math.min(this.levelWidth(), maxViewTiles);
    const viewportHeight = GAME_CONFIG.tileSize * Math.min(this.levelHeight(), maxViewTiles);
    const totalWidth = viewportWidth + GAME_CONFIG.statsPanelWidth;

    // Set camera bounds - extend by stats panel width to keep player visible at right edge
    camera.setBounds(
      0,
      0,
      worldWidth + GAME_CONFIG.statsPanelWidth,
      worldHeight
    );
    camera.setViewport(0, 0, totalWidth, viewportHeight);
    camera.setBackgroundColor(0x333333);

    // Follow player sprite if it exists
    const player = this.getPlayer();
    if (player?.sprite) {
      camera.startFollow(player.sprite, true, 0.1, 0.1);
      // Offset camera to keep player centered in visible game area (not including stats panel)
      camera.setFollowOffset(-GAME_CONFIG.statsPanelWidth / 2, 0);
    }
  }

  /**
   * Update camera to follow the current player sprite
   * Should be called after render() when sprites are recreated
   */
  updateCameraFollow(): void {
    const player = this.getPlayer();
    if (player?.sprite) {
      this.scene.cameras.main.startFollow(player.sprite, true, 0.1, 0.1);
    }
  }

  /**
   * Pause/resume methods for game state management
   */
  pause(): void {
    // No internal timers - controlled by InputSystem
  }

  resume(): void {
    // No internal timers - controlled by InputSystem
  }

  /**
   * Cleanup
   */
  destroy(): void {
    // Destroy all sprites
    for (const sprite of this.allSprites) {
      sprite.destroy();
    }
    this.allSprites = [];
  }

  /**
   * Get the player entity
   */
  getPlayer(): PlayerEntity | null {
    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const entity = this.currentEntityMap[row][col];
        if (entity instanceof PlayerEntity) {
          return entity;
        }
      }
    }
    return null;
  }

  /**
   * Get the player entity
   */
  getNextPlayer(): PlayerEntity | null {
    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const entity = this.nextEntityMap[row][col];
        if (entity instanceof PlayerEntity) {
          return entity;
        }
      }
    }
    return null;
  }

  /**
   * Get the current entity map (for legacy code compatibility)
   * Returns a read-only view of entity positions as TileIndex[][]
   */
  getCurrentEntityMap(): TileIndex[][] {
    return this.currentEntityMap.map(row =>
      row.map(entity => entity ? entity.tileType() : TILE.EMPTY)
    );
  }

  /**
   * Get sprite group for collision detection
   */
  getSpriteGroup(): Phaser.GameObjects.Sprite[] {
    const sprites: Phaser.GameObjects.Sprite[] = [];
    for (let row = 0; row < this.levelHeight(); row++) {
      for (let col = 0; col < this.levelWidth(); col++) {
        const entity = this.currentEntityMap[row][col];
        if (entity?.sprite) {
          sprites.push(entity.sprite);
        }
      }
    }
    return sprites;
  }

  /**
   * Remove an entity by its sprite (for collision handling)
   */
  removeEntityBySprite(sprite: Phaser.GameObjects.Sprite): void {
    // Get entity from sprite data
    const entity = sprite.getData("entity") as BaseTile | undefined;
    if (!entity) return;

    // Remove from maps
    this.currentEntityMap[entity.row][entity.col] = null;

    // Destroy sprite
    entity.destroySprite();
  }

  /**
   * Get win state - returns true if the level was won
   */
  getWinState(): boolean {
    return this.hasWon;
  }

  /**
   * Reset game state flags (called when level restarts)
   */
  resetGameState(): void {
    this.hasWon = false;
  }
}
